import { parseLocalDate, calculateAge } from './utils.js'

function isLeapYear(year) {
  return (year % 4 === 0 && year % 100 !== 0) || (year % 400 === 0)
}

function daysInYear(year) {
  return isLeapYear(year) ? 366 : 365
}

function parseDate(value) {
  return parseLocalDate(value)
}

function isEmployedBeforePeriod(emp, periodStart) {
  const hire = parseDate(emp.hire_date)
  if (!hire) return false
  if (hire >= periodStart) return false
  const term = parseDate(emp.termination_date)
  if (term && term < periodStart) return false
  return true
}

function isEmployedAfterPeriod(emp, periodEnd) {
  const hire = parseDate(emp.hire_date)
  if (!hire || hire > periodEnd) return false
  const term = parseDate(emp.termination_date)
  if (term && term <= periodEnd) return false
  return true
}

function hiredInPeriod(emp, periodStart, periodEnd) {
  const hire = parseDate(emp.hire_date)
  if (!hire) return false
  return hire >= periodStart && hire <= periodEnd
}

function terminatedInPeriod(emp, periodStart, periodEnd) {
  const term = parseDate(emp.termination_date)
  if (!term) return false
  return term >= periodStart && term <= periodEnd
}

function computePeriodStats(employees, periodStart, periodEnd, planYear, shortTermDays) {
  let starting = 0
  let ending = 0
  let hires = 0
  let terminations = 0
  let shortTermTerminations = 0
  const hireEmployeeIds = []
  const terminationEmployeeIds = []

  for (const emp of employees) {
    if (isEmployedBeforePeriod(emp, periodStart)) starting++
    if (isEmployedAfterPeriod(emp, periodEnd)) ending++
    if (hiredInPeriod(emp, periodStart, periodEnd)) {
      hires++
      hireEmployeeIds.push(emp.id)
    }
    if (terminatedInPeriod(emp, periodStart, periodEnd)) {
      terminations++
      terminationEmployeeIds.push(emp.id)
      const hire = parseDate(emp.hire_date)
      const term = parseDate(emp.termination_date)
      if (hire && term) {
        const tenureDays = Math.round((term - hire) / (1000 * 60 * 60 * 24))
        if (tenureDays >= 0 && tenureDays <= shortTermDays) shortTermTerminations++
      }
    }
  }

  const avgHeadcount = (starting + ending) / 2
  // Annualized turnover: expressed as annual rate regardless of period length
  const periodDays = Math.round((periodEnd - periodStart) / (1000 * 60 * 60 * 24)) + 1
  const annualizationFactor = avgHeadcount > 0 ? daysInYear(planYear) / periodDays : 0
  const turnoverRate = avgHeadcount > 0 ? (terminations / avgHeadcount) * annualizationFactor * 100 : 0

  return {
    starting,
    hires,
    terminations,
    ending,
    avgHeadcount: Math.round(avgHeadcount * 10) / 10,
    turnoverRate: Math.round(turnoverRate * 10) / 10,
    netChange: ending - starting,
    shortTermTerminations,
    hireEmployeeIds,
    terminationEmployeeIds,
    employeeIds: [...new Set([...hireEmployeeIds, ...terminationEmployeeIds])],
  }
}

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

function buildPeriods(planYear, granularity) {
  if (granularity === 'monthly') {
    return Array.from({ length: 12 }, (_, m) => ({
      label: MONTH_NAMES[m],
      start: new Date(planYear, m, 1),
      end: new Date(planYear, m + 1, 0), // last day of month
    }))
  }
  if (granularity === 'quarterly') {
    return [
      { label: 'Q1', start: new Date(planYear, 0, 1), end: new Date(planYear, 2, 31) },
      { label: 'Q2', start: new Date(planYear, 3, 1), end: new Date(planYear, 5, 30) },
      { label: 'Q3', start: new Date(planYear, 6, 1), end: new Date(planYear, 8, 30) },
      { label: 'Q4', start: new Date(planYear, 9, 1), end: new Date(planYear, 11, 31) },
    ]
  }
  if (granularity === 'semiannual') {
    return [
      { label: 'H1', start: new Date(planYear, 0, 1), end: new Date(planYear, 5, 30) },
      { label: 'H2', start: new Date(planYear, 6, 1), end: new Date(planYear, 11, 31) },
    ]
  }
  // annual
  return [
    { label: String(planYear), start: new Date(planYear, 0, 1), end: new Date(planYear, 11, 31) },
  ]
}

/**
 * Calculate turnover statistics from raw employee data.
 *
 * @param {object[]} employees - raw employee objects with hire_date, termination_date
 * @param {number} planYear
 * @param {number} shortTermThresholdDays - days threshold for "short-term" terminations
 * @returns {object} turnover data broken down by granularity
 */
export function calculateTurnover(employees, planYear, shortTermThresholdDays = 90) {
  const planYearEnd = new Date(planYear, 11, 31)
  let dateIssueCount = 0
  const withDates = employees.flatMap((employee) => {
    const hire = parseDate(employee.hire_date)
    if (!hire) {
      dateIssueCount++
      return []
    }

    const hasTerminationDate = Boolean(
      employee.termination_date && String(employee.termination_date).trim()
    )
    const isTerminated = employee.status === 'Terminated'

    if (!isTerminated) {
      if (hasTerminationDate) dateIssueCount++
      return [{ ...employee, termination_date: '' }]
    }

    const term = parseDate(employee.termination_date)
    if (!hasTerminationDate || !term || term < hire || term > planYearEnd) {
      dateIssueCount++
      return []
    }

    return [{ ...employee, termination_date: employee.termination_date }]
  })

  const buildGranularity = (granularity) =>
    buildPeriods(planYear, granularity).map(({ label, start, end }) => ({
      label,
      ...computePeriodStats(withDates, start, end, planYear, shortTermThresholdDays),
    }))

  // Annual short-term terminations (all terminations within plan year below threshold)
  const yearStart = new Date(planYear, 0, 1)
  const yearEnd = new Date(planYear, 11, 31)
  const allShortTerm = withDates.filter((emp) => {
    if (!terminatedInPeriod(emp, yearStart, yearEnd)) return false
    const hire = parseDate(emp.hire_date)
    const term = parseDate(emp.termination_date)
    if (!hire || !term) return false
    const tenureDays = Math.round((term - hire) / (1000 * 60 * 60 * 24))
    return tenureDays >= 0 && tenureDays <= shortTermThresholdDays
  })

  return {
    monthly: buildGranularity('monthly'),
    quarterly: buildGranularity('quarterly'),
    semiannual: buildGranularity('semiannual'),
    annual: buildGranularity('annual'),
    shortTermTerminations: allShortTerm.map((e) => {
      const hire = parseDate(e.hire_date)
      const term = parseDate(e.termination_date)
      return {
        id: e.id,
        name: e.name || '',
        age: calculateAge(e.dob, term || undefined),
        hireDate: e.hire_date,
        terminationDate: e.termination_date,
        tenureDays: hire && term ? Math.round((term - hire) / (1000 * 60 * 60 * 24)) : null,
      }
    }),
    dateIssueCount,
    statusDateIssueCount: dateIssueCount,
    shortTermThresholdDays,
  }
}
